import React, { useMemo, useState, useRef, useEffect, ReactNode } from 'react';

import { useCurrentProjectId, useCurrentProject } from '@adsk/alloy-provider';
import { Box } from '@adsk/alloy-react-box';
import { callOrReturn, warn } from '@adsk/alloy-react-helpers';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme from '@adsk/alloy-react-theme';

import { REFERENCE_PICKER_DISPLAY_VARIANTS } from './constants';
import { isPhotosPluginAndUserHasNoBuildAccess } from './helpers/isPhotosPluginAndUserHasNoBuildAccess';
import useRemoveEntity from './hooks/useRemoveEntity';
import { useTranslation } from './i18n';
import ReferencePickerEmptyState from './ReferencePickerEmptyState';
import ReferencePickerError from './ReferencePickerError';
import ReferencePickerLoader from './ReferencePickerLoader';
import ReferencePickerModalContainer from './ReferencePickerModalContainer';
import ReferencePickerTitle from './ReferencePickerTitle';
import ReferencePickerTopBar from './ReferencePickerTopBar';
import ReferencePickerTypeButton from './ReferencePickerTypeButton';
import RemoveConfirmationModal from './RemoveConfirmationModal';
import { CommonReferencePickerPlugin } from './types/ReferencePickerPlugin';
import {
  AddEntitiesToCacheProps,
  AddRelationships,
  CreateFormProps,
  EntitiesCache,
  EntityRelationship,
  Relationship,
  RelationshipDataWithState,
  UpdateEntitiesInCache,
} from './types/types';
import { AccordionVariant, ListVariant } from './variants';

interface ReferencePickerViewProps {
  style?: React.CSSProperties;
  className?: string;
  displayVariant?: string;
  sourceEntityId: string;
  sourceEntityDomain: string;
  sourceEntityType: string;
  targetPlugins: CommonReferencePickerPlugin[];
  container?: React.RefObject<HTMLElement> | null;
  allowAddingLinks?: boolean;
  allowRemoveLinkedEntity?: boolean | ((args: EntityRelationship) => boolean);
  onRemove: (args: Relationship) => Promise<void | string[]>;
  onAddRelationships: AddRelationships;
  title?: string | ReactNode;
  addButtonText?: string;
  emptyStateText?: ReactNode;
  createFormProps?:
    | CreateFormProps
    | ((args: { domain: string; type: string }) => CreateFormProps);
  dataViewProps?:
    | Record<string, unknown>
    | ((args: { domain: string; type: string }) => Record<string, unknown>);
  relationshipsData: RelationshipDataWithState;
  entitiesCache: EntitiesCache;
  addEntitiesToCache?: (option: AddEntitiesToCacheProps) => void;
  updateEntitiesInCache?: UpdateEntitiesInCache;
  onAddReferenceClick?: (args: { domain: string; type: string }) => void;
  onReferenceClickAnalytics?: (args: unknown) => void;
  loadingPlugins?: boolean;
}

/**
 * A ReferencePickerView
 * docs.
 */
const ReferencePickerView = React.forwardRef<
  HTMLDivElement,
  ReferencePickerViewProps
>((props, ref) => {
  const {
    className,
    style,
    displayVariant = REFERENCE_PICKER_DISPLAY_VARIANTS.LIST,
    sourceEntityId,
    sourceEntityDomain,
    sourceEntityType,
    container,
    targetPlugins,
    allowAddingLinks,
    allowRemoveLinkedEntity,
    onRemove,
    onAddRelationships,
    title,
    addButtonText,
    emptyStateText,
    createFormProps,
    dataViewProps,
    relationshipsData,
    entitiesCache,
    addEntitiesToCache,
    updateEntitiesInCache,
    onAddReferenceClick,
    onReferenceClickAnalytics,
    loadingPlugins,
  } = props;
  const shouldReportListDeprecationOnce = useRef(true);
  const { t } = useTranslation();
  const projectId = useCurrentProjectId();
  const currentProject = useCurrentProject();
  const [pickerModalPlugin, setPickerModalPlugin] =
    useState<CommonReferencePickerPlugin | null>(null);
  const openPickerModal = (plugin: CommonReferencePickerPlugin) => {
    setPickerModalPlugin(plugin);
    onAddReferenceClick?.(plugin.metadata);
  };
  const onClosePickerModal = () => {
    setPickerModalPlugin(null);
  };
  const listTitle = 'title' in props ? title : t('REFERENCE_PICKER_TITLE');
  const shouldRenderPickerModal = allowAddingLinks || pickerModalPlugin;

  const {
    loading: loadingRelationships,
    error: errorLoadingRelationships,
    retry: reloadRelationships,
    data: allRelationships,
  } = relationshipsData;

  const pickerModalRelationships = useMemo(
    () =>
      (pickerModalPlugin && allRelationships
        ? allRelationships.filter(
            ({ domain, type }) =>
              domain === pickerModalPlugin.metadata.domain &&
              type === pickerModalPlugin.metadata.type,
          )
        : []) || [],
    [pickerModalPlugin, allRelationships],
  );

  const pickerModalRelationshipData = useMemo(
    () => ({
      data: pickerModalRelationships,
      loading: loadingRelationships,
      error: errorLoadingRelationships,
      retry: reloadRelationships,
    }),
    [
      errorLoadingRelationships,
      loadingRelationships,
      pickerModalRelationships,
      reloadRelationships,
    ],
  );

  useEffect(() => {
    if (
      shouldReportListDeprecationOnce.current &&
      displayVariant === REFERENCE_PICKER_DISPLAY_VARIANTS.LIST
    ) {
      warn(
        'The reference picker design have been improved, old design will be deprecated soon. Please use this prop: displayVariant = REFERENCE_PICKER_DISPLAY_VARIANTS.ACCORDION',
      );
      shouldReportListDeprecationOnce.current = false;
    }
  }, [displayVariant]);
  const {
    dataToConfirmRemoving,
    setDataToConfirmRemoving,
    isRemoving,
    closeConfirmRemoveModal,
    handleRemoveConfirm,
  } = useRemoveEntity(onRemove);

  const pluginsSupportContent = targetPlugins.filter(
    (plugin) => !plugin.isPickerOnly,
  );

  const groupData = pluginsSupportContent.map((plugin) => ({
    plugin,
    entityCache:
      entitiesCache?.[plugin.metadata.domain]?.[plugin.metadata.type],
    relationships: allRelationships
      ? allRelationships.filter(
          ({ domain, type }) =>
            domain === plugin.metadata.domain && type === plugin.metadata.type,
        )
      : [],
  }));

  const isBlocksEmpty =
    !groupData.length || groupData.every((g) => !g.relationships.length);

  // using memo here for better readability, having a short condition here is very confusing
  const showMainLoader = useMemo(() => {
    if (loadingRelationships && isBlocksEmpty) {
      return true;
    }

    if (!projectId) {
      return true;
    }

    return false;
  }, [loadingRelationships, isBlocksEmpty, projectId]);

  const showEmptyState =
    !showMainLoader && !errorLoadingRelationships && isBlocksEmpty;
  const showContent =
    !showMainLoader && !errorLoadingRelationships && !isBlocksEmpty;

  const pluginsAllowingSearch = targetPlugins.filter((plugin) => {
    if (isPhotosPluginAndUserHasNoBuildAccess({ plugin, currentProject })) {
      return false;
    }
    return plugin.queryEntities;
  });

  const addText = addButtonText || t('ADD_REFERENCES');

  const groupDataToDisplay = useMemo(
    () =>
      groupData.filter(({ relationships }) => {
        return relationships && relationships.length;
      }),
    [groupData],
  );

  return (
    <Box
      ref={ref}
      className={className}
      border={`1px solid ${theme.colors.charcoal200}`}
      borderRadius="2px"
      style={style}
    >
      {(listTitle || allowAddingLinks) && (
        <ReferencePickerTopBar>
          {listTitle && (
            <ReferencePickerTitle>
              {listTitle}
              {displayVariant ===
              REFERENCE_PICKER_DISPLAY_VARIANTS.ACCORDION ? (
                <>
                  {' '}
                  (
                  {groupDataToDisplay.reduce(
                    (acc, { relationships }) => acc + relationships.length,
                    0,
                  )}
                  )
                </>
              ) : null}
            </ReferencePickerTitle>
          )}
          {allowAddingLinks && (
            <ReferencePickerTypeButton
              openPickerModal={openPickerModal}
              plugins={pluginsAllowingSearch}
              loadingPlugins={loadingPlugins}
              container={container}
            >
              {addText}
            </ReferencePickerTypeButton>
          )}
        </ReferencePickerTopBar>
      )}

      <div>
        {showContent &&
          (displayVariant === REFERENCE_PICKER_DISPLAY_VARIANTS.LIST ? (
            <ListVariant
              groupData={groupDataToDisplay}
              allowRemoveLinkedEntity={allowRemoveLinkedEntity}
              setDataToConfirmRemoving={setDataToConfirmRemoving}
              updateEntitiesInCache={updateEntitiesInCache}
              onReferenceClickAnalytics={onReferenceClickAnalytics}
              sourceEntityId={sourceEntityId}
              sourceEntityDomain={sourceEntityDomain}
              sourceEntityType={sourceEntityType}
            />
          ) : (
            <AccordionVariant
              groupData={groupDataToDisplay}
              allowRemoveLinkedEntity={allowRemoveLinkedEntity}
              setDataToConfirmRemoving={setDataToConfirmRemoving}
              updateEntitiesInCache={updateEntitiesInCache}
              onReferenceClickAnalytics={onReferenceClickAnalytics}
              sourceEntityId={sourceEntityId}
              sourceEntityDomain={sourceEntityDomain}
              sourceEntityType={sourceEntityType}
            />
          ))}
        {showMainLoader && <ReferencePickerLoader />}
        {errorLoadingRelationships && (
          <ReferencePickerError onTryAgain={reloadRelationships} />
        )}
        {showEmptyState && (
          <ReferencePickerEmptyState
            emptyStateText={emptyStateText}
            plugins={pluginsAllowingSearch}
            allowAddingLinks={allowAddingLinks}
          />
        )}
      </div>
      {shouldRenderPickerModal && (
        <ReferencePickerModalContainer
          sourceEntityId={sourceEntityId}
          sourceEntityDomain={sourceEntityDomain}
          sourceEntityType={sourceEntityType}
          plugin={pickerModalPlugin}
          open={!!pickerModalPlugin}
          closeModal={onClosePickerModal}
          onAddRelationships={onAddRelationships}
          relationshipsData={pickerModalRelationshipData}
          addEntitiesToCache={addEntitiesToCache}
          updateEntitiesInCache={updateEntitiesInCache}
          container={container}
          dataViewProps={
            dataViewProps && pickerModalPlugin
              ? callOrReturn(dataViewProps, {
                  domain: pickerModalPlugin.metadata.domain,
                  type: pickerModalPlugin.metadata.type,
                })
              : undefined
          }
          createFormProps={
            createFormProps && pickerModalPlugin
              ? callOrReturn(createFormProps, {
                  domain: pickerModalPlugin.metadata.domain,
                  type: pickerModalPlugin.metadata.type,
                })
              : undefined
          }
        />
      )}
      {showContent && (
        <RemoveConfirmationModal
          plugin={dataToConfirmRemoving?.plugin}
          entity={dataToConfirmRemoving?.entity}
          container={container}
          closeConfirmRemoveModal={closeConfirmRemoveModal}
          onRemove={handleRemoveConfirm}
          isRemoving={isRemoving}
        />
      )}
    </Box>
  );
});

ReferencePickerView.displayName = 'ReferencePickerView';

export default WithAlloySuspense(ReferencePickerView);
